import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  ChevronLeft,
  Upload,
  Trash2,
  Calendar,
  Clock,
  Lock,
  ImageIcon,
  Eye,
  EyeOff,
  Loader2,
  Users,
  Instagram,
  Hash,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditAlbumPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Loading state
  const [loading, setLoading] = useState(true);
  const [album, setAlbum] = useState(null);
  
  // Form states
  const [albumName, setAlbumName] = useState("");
  const [photoLimit, setPhotoLimit] = useState("300");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [eventDate, setEventDate] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  
  // Massive event states
  const [isMassiveEvent, setIsMassiveEvent] = useState(false);
  const [massiveEventName, setMassiveEventName] = useState("");
  const [massiveEventInstagram, setMassiveEventInstagram] = useState("");
  const [massiveEventHashtags, setMassiveEventHashtags] = useState("");
  const [massiveEventMessage, setMassiveEventMessage] = useState("");
  const [massiveEventStartDate, setMassiveEventStartDate] = useState("");
  const [massiveEventEndDate, setMassiveEventEndDate] = useState("");
  
  const coverInputRef = useRef(null);

  // Fetch album data
  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        const response = await axios.get(`${API}/admin/albums`);
        const foundAlbum = response.data.find(a => a.share_code === code);
        
        if (foundAlbum) {
          setAlbum(foundAlbum);
          setAlbumName(foundAlbum.name);
          setPhotoLimit(foundAlbum.photo_limit === null ? "unlimited" : String(foundAlbum.photo_limit));
          setPassword(foundAlbum.password_plain || "");
          setEventDate(foundAlbum.event_date || "");
          setCoverImage(foundAlbum.cover_image_url || null);
          
          // Load massive event data
          setIsMassiveEvent(foundAlbum.is_massive_event || false);
          setMassiveEventName(foundAlbum.massive_event_name || "");
          setMassiveEventInstagram(foundAlbum.massive_event_instagram || "");
          setMassiveEventHashtags(foundAlbum.massive_event_hashtags || "");
          setMassiveEventMessage(foundAlbum.massive_event_message || "");
          setMassiveEventStartDate(foundAlbum.massive_event_start_date || "");
          setMassiveEventEndDate(foundAlbum.massive_event_end_date || "");
        } else {
          toast.error(t('albumNotFound'));
          navigate('/admin');
        }
      } catch (error) {
        console.error('Failed to fetch album:', error);
        toast.error(t('failedToLoad'));
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAlbum();
  }, [code, navigate, t]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/albums/${code}/cover`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCoverImage(response.data.cover_image_url);
      toast.success(t('coverImageUploaded') || 'Portada actualizada');
    } catch (error) {
      toast.error(t('failedToUploadImage'));
      console.error(error);
    } finally {
      setUploadingCover(false);
      e.target.value = '';
    }
  };

  const handleDeleteCover = async () => {
    try {
      await axios.delete(`${API}/albums/${code}/cover`);
      setCoverImage(null);
      toast.success(t('coverImageDeleted') || 'Portada eliminada');
    } catch (error) {
      toast.error(t('failedToDelete'));
      console.error(error);
    }
  };

  const handleSave = async () => {
    // Validate based on mode
    if (isMassiveEvent) {
      if (!massiveEventName.trim()) {
        toast.error(t('enterEventName') || 'Ingresa el nombre del evento');
        return;
      }
    } else {
      if (!albumName.trim()) {
        toast.error(t('albumNameEmpty'));
        return;
      }
    }

    if (!password.trim()) {
      toast.error(t('passwordRequired'));
      return;
    }
    
    setIsSaving(true);
    try {
      const limit = photoLimit === "unlimited" ? null : parseInt(photoLimit);
      await axios.put(`${API}/albums/${code}`, {
        name: isMassiveEvent ? massiveEventName.trim() : albumName.trim(),
        description: album?.description || "",
        photo_limit: limit,
        is_private: true,
        password: password.trim(),
        event_date: isMassiveEvent ? null : (eventDate || null),
        is_massive_event: isMassiveEvent,
        massive_event_name: isMassiveEvent ? massiveEventName.trim() : null,
        massive_event_instagram: isMassiveEvent ? massiveEventInstagram : null,
        massive_event_hashtags: isMassiveEvent ? massiveEventHashtags : null,
        massive_event_message: isMassiveEvent ? massiveEventMessage : null,
        massive_event_start_date: isMassiveEvent ? massiveEventStartDate : null,
        massive_event_end_date: isMassiveEvent ? massiveEventEndDate : null
      });
      
      toast.success(t('albumUpdated'));
      navigate('/admin');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("Password")) {
        toast.error(t('passwordRequired'));
      } else {
        toast.error(t('failedToUpdate'));
      }
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          <p className="text-slate-600">{t('loading')}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{t('cancel')}</span>
          </button>
          <div className="flex items-center gap-2">
            <img src="/mf-logo.png" alt="MFcreativemanager" className="w-8 h-8 object-contain" />
            <span className="font-bold text-slate-900 hidden sm:block" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('appName')}
            </span>
          </div>
          <LanguageSwitcher variant="minimal" />
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-24 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 
              className="text-2xl sm:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('editAlbum')}
            </h1>
            <p className="text-slate-600 mt-2">
              {t('code')}: <span className="font-mono font-semibold text-indigo-600">{code}</span>
            </p>
          </div>

          {/* Form */}
          <div className="space-y-6 bg-white rounded-2xl p-6 shadow-sm">
            
            {/* Event Type Selection - FIRST */}
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isMassiveEvent ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                    <Users className={`w-5 h-5 ${isMassiveEvent ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{t('massiveEventMode')}</p>
                    <p className="text-sm text-slate-500">{t('massiveEventDesc')}</p>
                  </div>
                </div>
                <Switch
                  checked={isMassiveEvent}
                  onCheckedChange={setIsMassiveEvent}
                  data-testid="edit-massive-event-switch"
                />
              </div>
            </div>

            {isMassiveEvent ? (
              /* ========== MASSIVE EVENT FORM ========== */
              <>
                {/* Massive Event Name */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
                    {t('massiveEventName')}
                  </label>
                  <Input
                    type="text"
                    value={massiveEventName}
                    onChange={(e) => setMassiveEventName(e.target.value)}
                    placeholder={t('massiveEventNamePlaceholder')}
                    className="h-12 rounded-xl text-lg"
                    data-testid="edit-massive-event-name"
                  />
                </div>

                {/* Instagram & Hashtags Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-2 flex items-center gap-2">
                      <Instagram className="w-4 h-4" />
                      {t('massiveEventInstagram')}
                    </label>
                    <Input
                      type="text"
                      value={massiveEventInstagram}
                      onChange={(e) => setMassiveEventInstagram(e.target.value)}
                      placeholder={t('massiveEventInstagramPlaceholder')}
                      className="h-12 rounded-xl"
                      data-testid="edit-massive-event-instagram"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-2 flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      {t('massiveEventHashtags')}
                    </label>
                    <Input
                      type="text"
                      value={massiveEventHashtags}
                      onChange={(e) => setMassiveEventHashtags(e.target.value)}
                      placeholder={t('massiveEventHashtagsPlaceholder')}
                      className="h-12 rounded-xl"
                      data-testid="edit-massive-event-hashtags"
                    />
                  </div>
                </div>

                {/* Message for guests */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    {t('massiveEventMessage')}
                  </label>
                  <textarea
                    value={massiveEventMessage}
                    onChange={(e) => setMassiveEventMessage(e.target.value)}
                    placeholder={t('massiveEventMessagePlaceholder')}
                    className="w-full h-24 rounded-xl border border-slate-200 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="edit-massive-event-message"
                  />
                </div>

                {/* Date Range */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t('massiveEventDuration')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">{t('massiveEventStartDate')}</label>
                      <Input
                        type="date"
                        value={massiveEventStartDate}
                        onChange={(e) => setMassiveEventStartDate(e.target.value)}
                        className="h-12 rounded-xl"
                        data-testid="edit-massive-event-start-date"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">{t('massiveEventEndDate')}</label>
                      <Input
                        type="date"
                        value={massiveEventEndDate}
                        onChange={(e) => setMassiveEventEndDate(e.target.value)}
                        className="h-12 rounded-xl"
                        data-testid="edit-massive-event-end-date"
                      />
                    </div>
                  </div>
                </div>

                {/* Photo Limit for Massive Event */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
                    {t('photoLimit')}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {["300", "500", "1000", "unlimited"].map((limit) => (
                      <button
                        key={limit}
                        onClick={() => setPhotoLimit(limit)}
                        className={`py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                          photoLimit === limit
                            ? "bg-indigo-600 text-white shadow-lg"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                        data-testid={`edit-limit-${limit}`}
                      >
                        {limit === "unlimited" ? "∞" : limit}
                      </button>
                    ))}
                  </div>
                  {album && (
                    <p className="text-xs text-slate-500 mt-2">
                      {t('currentPhotos')}: {album.photo_count || 0}
                    </p>
                  )}
                </div>

                {/* Password for Massive Event */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <Lock className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-semibold text-slate-900">
                        {t('albumPassword') || 'Contraseña del Álbum'}
                      </p>
                      <p className="text-sm text-slate-500">
                        {t('albumPasswordDesc') || 'El anfitrión usará esta contraseña para administrar el álbum'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-2">
                      {t('password')}
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('enterPassword')}
                        className="h-12 rounded-xl pr-12"
                        data-testid="edit-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Cover Image for Massive Event */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
                    {t('massiveEventCoverImage')}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    ref={coverInputRef}
                    className="hidden"
                    onChange={handleCoverUpload}
                  />
                  {coverImage ? (
                    <div className="relative rounded-xl overflow-hidden">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${coverImage}`}
                        alt="Cover preview" 
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => coverInputRef.current?.click()}
                          disabled={uploadingCover}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          {t('change') || 'Cambiar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={handleDeleteCover}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {t('remove') || 'Eliminar'}
                        </Button>
                      </div>
                      {uploadingCover && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => coverInputRef.current?.click()}
                      className="w-full h-48 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-indigo-400 hover:text-indigo-500 transition-colors"
                    >
                      <ImageIcon className="w-8 h-8" />
                      <span className="text-sm font-medium">{t('massiveEventUploadCover')}</span>
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* ========== NORMAL ALBUM FORM ========== */
              <>
                {/* Album Name */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
                    {t('albumName')}
                  </label>
                  <Input
                    value={albumName}
                    onChange={(e) => setAlbumName(e.target.value)}
                    placeholder={t('albumNamePlaceholder')}
                    className="h-12 rounded-xl text-lg"
                    data-testid="edit-album-name"
                  />
                </div>

                {/* Photo Limit */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
                    {t('photoLimit')}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {["100", "300", "500", "unlimited"].map((limit) => (
                      <button
                        key={limit}
                        onClick={() => setPhotoLimit(limit)}
                        className={`py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                          photoLimit === limit
                            ? "bg-indigo-600 text-white shadow-lg"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                        data-testid={`edit-limit-${limit}`}
                      >
                        {limit === "unlimited" ? "∞" : limit}
                      </button>
                    ))}
                  </div>
                  {album && (
                    <p className="text-xs text-slate-500 mt-2">
                      {t('currentPhotos')}: {album.photo_count || 0}
                    </p>
                  )}
                </div>

                {/* Password - Always Required */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <Lock className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-semibold text-slate-900">
                        {t('albumPassword') || 'Contraseña del Álbum'}
                      </p>
                      <p className="text-sm text-slate-500">
                        {t('albumPasswordDesc') || 'El anfitrión usará esta contraseña para administrar el álbum'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-2">
                      {t('password')}
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('enterPassword')}
                        className="h-12 rounded-xl pr-12"
                        data-testid="edit-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Event Date */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {t('eventDate')}
                  </label>
                  <Input
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    className="h-12 rounded-xl"
                    data-testid="edit-event-date"
                  />
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t('eventDateOptional')}
                  </p>
                </div>

                {/* Cover Image */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
                    <span className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      {t('coverImage')}
                    </span>
                  </label>
                  
                  {coverImage ? (
                    <div className="relative rounded-xl overflow-hidden bg-slate-100">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${coverImage}`}
                        alt="Cover"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => coverInputRef.current?.click()}
                          disabled={uploadingCover}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          {t('change') || 'Cambiar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={handleDeleteCover}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {t('remove') || 'Eliminar'}
                        </Button>
                      </div>
                      {uploadingCover && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 transition-colors"
                      onClick={() => coverInputRef.current?.click()}
                    >
                      {uploadingCover ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                          <p className="text-slate-600">{t('uploading')}...</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                          <p className="text-slate-600 font-medium">{t('uploadCover')}</p>
                          <p className="text-sm text-slate-400 mt-1">{t('coverImageOptional')}</p>
                        </>
                      )}
                    </div>
                  )}
                  
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCoverUpload}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-2xl mx-auto flex gap-3">
          <Button
            variant="outline"
            className="flex-1 h-12 rounded-xl"
            onClick={() => navigate('/admin')}
          >
            {t('cancel')}
          </Button>
          <Button
            className="flex-1 h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-pink-600 text-white"
            onClick={handleSave}
            disabled={isSaving}
            data-testid="save-album-submit"
          >
            {isSaving ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('saving')}...
              </div>
            ) : (
              t('saveChanges')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
